import { useLocation } from 'react-router-dom'
import HeaderNavBar from './HeaderNavBar'
import LogoHeader from './LogoHeader'
import { isHideGlobalHeaderPath, isProline } from '../../../helpers'
import { useEffect } from 'react'
import { useSiteContext } from '../../../context/site/SiteProvider'
import { Helmet } from 'react-helmet'
import {
  DEFAULT_PL_TITLE,
  DEFAULT_WS_TITLE,
} from '../../../constants/constants'
import { useQueryClient } from '@tanstack/react-query'
import { userKeys } from '../../../queries/user/keyFactory'
import { productKeys } from '../../../queries/products/keyFactory'
import { ICustomerData, IUserProfile } from '../../../queries/user/types'
import { useProductContext } from '../../../context/products/ProductProvider'
import { IProductData } from '../../../context/products/types'
import { cartKeys } from '../../../queries/cart/keyFactory'
import { CLASS_OT_SUBTOTAL, CLASS_OT_TOTAL } from '../../../constants/order'
import CartTotals from '../Cart/CartTotals'
import { GET_CART_TOTALS } from '../../../context/products/constants'

// Extend Window interface for TypeScript
declare global {
  interface Window {
    iAdvizeInterface: any;
    idzCustomData: any;
    onresize: any;
    dataLayer: any[];
  }
}

const Header = () => {
  const location = useLocation()

  const {
    setMainHeaderDivHeight,
    setMobileMainHeaderDivHeight,
    getFlashSaleBanners,
    getPageSeo,
    setIdzCustomData,
    mainHeaderDivHeight,
    mobileMainHeaderDivHeight,
    isDownForMaintenance,
    pageSeo,
    idzCustomData,
  } = useSiteContext()

  const queryClient = useQueryClient()
  const userProfile = queryClient.getQueryData(
    userKeys.userProfile(),
  ) as IUserProfile
  const customerData = queryClient.getQueryData(
    userKeys.customer(),
  ) as ICustomerData

  const { productUrl } = useProductContext()

  // Helper to bridge iAdvize to the App.tsx listener
  const iAdvizeAddToCart = (productId: string) => {
    return new Promise((resolve, reject) => {
      const event = new CustomEvent('iadvize:addToCart', {
        detail: {
          productId,
          resolve,
          reject,
        },
      });
      window.dispatchEvent(event);
    });
  };

  /**
   * DATA LAYER SYNC:
   * Keeps GTM updated whenever user, cart, or SEO data changes.
   */
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    
    window.dataLayer.push({
      event: 'site_data_update',
      user: {
        id: userProfile?.customers_id?.toString() || '',
        email: userProfile?.customers_email_address || '',
        price_level: customerData?.price_levels_id?.toString() || '16',
      },
      cart: {
        id: customerData?.customers_carts_id?.toString() || '',
      },
      page: {
        subtotal: (pageSeo as any)?.order_subtotal || '',
        total: (pageSeo as any)?.order_total || '',
        order_number: (pageSeo as any)?.confirmation_number || '',
      }
    });
  }, [userProfile, customerData, pageSeo]);

  useEffect(() => {
    getFlashSaleBanners()
  }, [])

  useEffect(() => {
    if (location.pathname) {
      getPageSeo(location.pathname)
    }
    getFlashSaleBanners()
  }, [location.pathname])

  /**
   * Main iAdvize Initialization & User Data Sync
   */
  useEffect(() => {
    const customData = {
      ...idzCustomData,
      customersId:
        userProfile && userProfile?.customers_id !== undefined
          ? userProfile.customers_id.toString()
          : '',
      cust_email: userProfile?.customers_email_address ?? '',
      priceLevel:
        customerData && customerData?.price_levels_id !== undefined
          ? customerData?.price_levels_id.toString()
          : '16',
      cust_name:
        (userProfile?.customers_firstname
          ? userProfile.customers_firstname
          : '') +
        (userProfile?.customers_lastname
          ? ' ' + userProfile.customers_lastname
          : ''),
      cust_firstname: userProfile?.customers_firstname ?? '',
    }

    window.idzCustomData = customData
    setIdzCustomData(customData)

    window.iAdvizeInterface = window.iAdvizeInterface || []
    window.iAdvizeInterface.push(function (iAdvize: any) {
      iAdvize.navigate('')

      // --- ADD-TO-CART EVENT REGISTRATION ---
      iAdvize.on("addToCart:clicked", ({ productId, resolve, reject }: any) => {
        iAdvizeAddToCart(productId)
          .then((success) => {
            // success is the boolean returned from the resolve() call in App.tsx
            resolve(!!success); 
          })
          .catch((error) => {
            reject(error);
          });
      });
    })

  }, [userProfile, customerData])

  /**
   * Product Page Specific iAdvize Logic
   */
  useEffect(() => {
    const handleProductData = async () => {
      if (productUrl) {
        const productData = (await queryClient.fetchQuery(
          productKeys.product(productUrl),
        )) as IProductData

        if (productData) {
          if (!isProline()) {
            window.iAdvizeInterface.config = { sid: 8948, lang: 'en' }
          } else {
            window.iAdvizeInterface.config = { sid: 8961, lang: 'en', theme: 'proline' }
          }

          const customData = {
            ...idzCustomData,
            product_id: productData?.products_id.toString(),
            model: productData?.products_model,
          }
          window.idzCustomData = customData
          setIdzCustomData(customData)

          window.iAdvizeInterface = window.iAdvizeInterface || []
          window.iAdvizeInterface.push(function (iAdvize: any) {
            iAdvize.navigate('')
          })

          // Inject SDK Script if not present
          if (!document.getElementById('iadvize-js')) {
            const iadvize_script = document.createElement('script')
            iadvize_script.id = 'iadvize-js'
            iadvize_script.src = '//halc.iadvize.com/iadvize.js'
            iadvize_script.async = true
            document.body.appendChild(iadvize_script)
          }
        }
      }
    }

    handleProductData()
  }, [productUrl])

  window.onresize = () => {
    setMainHeaderDivHeight(mainHeaderDivHeight)
    setMobileMainHeaderDivHeight(mobileMainHeaderDivHeight)
  }

  const title = isProline() ? DEFAULT_PL_TITLE : DEFAULT_WS_TITLE

  return (
    <>
      <Helmet>
        {pageSeo?.id && pageSeo?.page_title && (
          <title>{title} - {pageSeo?.page_title}</title>
        )}
        {pageSeo?.id && pageSeo?.seo_meta && (
          <meta name="description" content={pageSeo?.seo_meta} />
        )}
        <meta name="iadvizeCustomersId" content={userProfile?.customers_id ? userProfile.customers_id.toString() : ''} />
        <meta name="iadvizePriceLevelsId" content={customerData?.price_levels_id ? customerData.price_levels_id.toString() : '16'} />
        <meta name="iadvizeTransactionId" content="" />
        <meta name="cart_id" id="gtm_cart_id" content={customerData?.customers_carts_id?.toString() || ''} />
  
        {/* These will likely be empty until the user is on the checkout/success page */}


         <meta name="order_subtotal" id="gtm_subtotal" content={(pageSeo as any)?.order_subtotal || ''} />
         <meta name="order_total" id="gtm_total" content={(pageSeo as any)?.order_total || ''} />
         <meta name="order_confirmation_number" id="gtm_conf_num" content={(pageSeo as any)?.confirmation_number || ''} />

         <meta name="order_subtotal" id="gtm_subtotal" content={'cart total'} />
         <meta name="order_total" id="gtm_total" content={ 'order total'} />
         <meta name="order_confirmation_number" id="gtm_conf_num" content={(pageSeo as any)?.confirmation_number || 'I Rule'} />

         <meta name="order_subtotal" id="gtm_subtotal" content={CLASS_OT_SUBTOTAL || ''} />
         <meta name="order_total" id="gtm_total" content={CLASS_OT_TOTAL || ''} />
         <meta name="order_confirmation_number" id="gtm_conf_num" content={(pageSeo as any)?.confirmation_number || 'I Rule'} />

  
      </Helmet>
      {isHideGlobalHeaderPath(location.pathname) || isDownForMaintenance ? (
        <LogoHeader linkToHome={!isDownForMaintenance} />
      ) : (
        <HeaderNavBar />
      )}
    </>
  )
}

export default Header